/** Shared route/nav-key vocabulary for the sidebar, bottom nav and hash router. */
/**
 * Разделы зрительской части. Настроек здесь нет: их — и общие, и личные —
 * задаёт администратор в админке, а пункт меню, ведущий на страницу без единой
 * настройки, обещал бы обратное.
 */
export const NAV_KEYS = ['home', 'movies', 'tv', 'livetv', 'favorites', 'continue'] as const;

export type NavKey = (typeof NAV_KEYS)[number];

const NAV_KEY_SET: ReadonlySet<string> = new Set(NAV_KEYS);

export function isNavKey(value: string): value is NavKey {
  return NAV_KEY_SET.has(value);
}

/**
 * Разделы админки — единственного экрана управления установкой. Настройки здесь
 * такой же раздел, как люди и устройства: две двери в одну комнату означали бы,
 * что за настройками надо помнить, в какую из них идти.
 */
export const ADMIN_SECTIONS = ['monitor', 'users', 'devices', 'settings', 'audit', 'logs'] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

export function isAdminSection(value: string): value is AdminSection {
  return (ADMIN_SECTIONS as readonly string[]).includes(value);
}

/**
 * Адрес админки: `#/admin`, `#/admin/<раздел>`, `#/admin/settings/<userId>`.
 * `null` — это не админка.
 *
 * Раздел живёт в адресе, а не только в состоянии страницы: «посмотри вот это
 * устройство» — нормальная просьба, а ссылка, которая открывается на другом
 * разделе, ею не является. Заодно работает кнопка «назад».
 *
 * Неизвестный раздел — это не ошибка и не пустой экран: показываем людей, с
 * которых админка и начинается.
 */
export function parseAdminHash(hash: string): { section: AdminSection; userId: string | null } | null {
  const [first, second, third] = hash.replace(/^#\/?/, '').split('/');
  if (first !== 'admin') return null;
  const section = second !== undefined && isAdminSection(second) ? second : 'users';
  return { section, userId: third ? decodeURIComponent(third) : null };
}

/**
 * Очень лёгкий hash-роутер (без внешней зависимости).
 *
 * Маршруты:
 *   #/                 — главная/каталог
 *   #/title/<id>[/sources|/episodes[/online|/torrent]]
 *                      — страница фильма/сериала и её шаги выбора. Шаг стоит В
 *                        АДРЕСЕ намеренно: «назад» в браузере тогда возвращает на
 *                        шаг назад, а не выкидывает со страницы, и отдельная
 *                        кнопка «свернуть» становится не нужна.
 *   #/watch/<sessionId>[/<contentId>]
 *                      — плеер уже выданной playback-сессии
 *   #/movies, #/tv,
 *   #/favorites,
 *   #/continue         — пункты меню без собственного backend-эндпоинта:
 *                         показывают честный coming-soon state вместо
 *                         вымышленных данных.
 *   #/admin[/<раздел>[/<userId>]]
 *                      — единственный экран управления установкой: люди,
 *                        устройства, настройки, журнал, логи.
 *
 * Hash-навигация не требует server-side fallback: SPA отдаётся одним
 * index.html, а содержимое меняется в браузере.
 */

export function parseHash(hash: string): {
  name: string;
  contentId: string | null;
  sessionId: string | null;
  /** Смотреть с начала, не возобновляя сохранённую позицию. */
  restart: boolean;
  /** Шаг страницы тайтла: `null` — описание. */
  step: 'sources' | 'episodes' | null;
  /** Класс источников на шаге выбора: `null` — не выбирался. */
  kind: 'online' | 'torrent' | null;
  /** Идентификатор человека на странице фильмографии; `null` — она не открыта. */
  personId: string | null;
} {
  const raw = hash.replace(/^#\/?/, '');
  const nowhere = {
    contentId: null, sessionId: null, restart: false, step: null, kind: null, personId: null,
  } as const;
  if (!raw) return { name: 'home', ...nowhere };
  const [first, second, third, fourth] = raw.split('/');
  if (first === 'livetv') return { name: 'livetv', ...nowhere };
  if (first === 'title' && second) {
    return {
      name: 'title',
      contentId: decodeURIComponent(second),
      sessionId: null,
      restart: false,
      step: third === 'sources' || third === 'episodes' ? third : null,
      // Класс источников — в адресе, а не только в памяти страницы.
      //
      // Иначе он терялся при любом возврате на карточку: «назад» из плеера
      // перемонтирует страницу, класс сбрасывался в значение по умолчанию, и
      // автопоиск по адресному шагу уводил на ТОРРЕНТЫ зрителя, который смотрел
      // онлайн. Адрес переживает и перемонтирование, и «назад/вперёд», и
      // перезагрузку — а память страницы не переживает ничего.
      kind: fourth === 'online' || fourth === 'torrent' ? fourth : null,
      personId: null,
    };
  }
  // Фильмография человека: сюда приводит нажатие на актёра в титрах карточки.
  if (first === 'person' && second) {
    return { name: 'person', ...nowhere, personId: decodeURIComponent(second) };
  }
  // Плеер открывается только по уже выданной сессии: без неё играть нечего,
  // а «угадать» поток из URL нельзя — путь потока выдаёт сервер.
  if (first === 'watch' && second) {
    return {
      name: 'watch',
      contentId: third ? decodeURIComponent(third) : null,
      sessionId: decodeURIComponent(second),
      // Хвост `/restart` — просьба НЕ возобновлять: её отдаёт кнопка «Смотреть
      // с начала». Без него плеер продолжил бы с сохранённой позиции, и подпись
      // на кнопке оказалась бы враньём.
      restart: fourth === 'restart',
      step: null,
      kind: null,
      personId: null,
    };
  }
  if (first && isNavKey(first)) return { name: first, ...nowhere };
  return { name: 'home', ...nowhere };
}
